import React, {useMemo} from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useI18n} from '@udemy/i18n';
import {GwgCheckoutProvider, GrowWithGoogleBannerWrapper} from '@udemy/subscription-browse';
import {useActiveSubscriptions} from '@udemy/subscription-graphql';

import {GwgFooter} from 'src/components/grow-with-google/gwg-footer/gwg-footer.react-component';
import {gwgAcceleratorToLearningPath} from 'src/components/grow-with-google/learning-path/adapters/gwg-accelerator-to-learning-path';
import {CourseCurriculums} from 'src/components/grow-with-google/learning-path/course-curriculums.react-component';
import {GrowWithGoogleTestimonials} from 'src/components/grow-with-google/testimonials/testimonials.react-component';
import {TopicLinks} from 'src/features/career-academy-landing-page/components/topic-links/topic-links.react-component';
import {Topic} from 'src/features/career-academy-landing-page/components/topic-links/types';
import {LearningStepsSection} from 'src/features/google-ai-path-page/components/learning-steps-section/learning-steps-section.react-component';
import {PathsLpHero} from 'src/features/google-ai-path-page/components/paths-lp-hero/paths-lp-hero.react-component';
import styles from 'src/features/google-ai-path-page/google-ai-path-page.module.less';
import {useCourseCertificateUrlById} from 'src/features/google-ai-path-page/hooks/use-course-certificate-url-by-id';
import {useCourseProgressById} from 'src/features/google-ai-path-page/hooks/use-course-progress-by-id';
import {ApiInstructor} from 'src/features/google-ai-path-page/types/course';
import {
    findFirstIncompleteCourse,
    getCompletedCoursesCount,
} from 'src/features/google-ai-path-page/utils/course-progress';
import {isSubscriberToAnyGwgPlan} from 'src/features/google-ai-plan-page/utils/is-gwg-subscriber';
import {computePlanStats} from 'src/features/google-ai-plan-page/utils/plan-stats';
import {useCoursesQuery} from 'src/generated/graphql';
import {useGwgPlanPreference} from 'src/hooks/useGwgPlanPreference';

import type {CourseCertificateEligibilityInput} from 'src/components/grow-with-google/learning-path/utils/course-certificate';

export interface GoogleAiPathPageProps {
    accelerator: GwgAccelerator;
    instructor?: ApiInstructor;
    topics?: Topic[];
    isEnrolled?: boolean;
    allGwgAccelerators?: GwgAccelerator[];
}

export function GoogleAiPathPage({
    accelerator,
    topics,
    instructor,
    isEnrolled,
    allGwgAccelerators,
}: GoogleAiPathPageProps) {
    const activeSubscription = useActiveSubscriptions()[0];
    const isGwgSubscriber = isSubscriberToAnyGwgPlan(activeSubscription);
    const enrolled = Boolean(isEnrolled);
    const courseIds = useMemo(
        () =>
            (accelerator.curriculum?.sections ?? [])
                .map((section) => (section.buyable?.id ? String(section.buyable.id) : null))
                .filter(Boolean) as string[],
        [accelerator.curriculum?.sections],
    );

    const {data: coursesData} = useCoursesQuery({ids: courseIds}, {enabled: courseIds.length > 0});
    const {courseTakingById, courseById} = useMemo(() => {
        const takingEntries: Record<string, string | undefined> = {};
        const courseEntries: Record<string, CourseCertificateEligibilityInput | undefined> = {};
        (coursesData?.courses ?? []).forEach((course) => {
            if (course?.id) {
                takingEntries[course.id] = course.urlCourseTaking ?? undefined;
                courseEntries[course.id] = {
                    isFree: course.isFree,
                    hasCertificate: course.hasCertificate,
                };
            }
        });
        return {courseTakingById: takingEntries, courseById: courseEntries};
    }, [coursesData]);

    const {courseProgressById} = useCourseProgressById(courseIds);
    const hasCompletedCourses = useMemo(
        () =>
            Object.values(courseProgressById).some(
                (progress) => typeof progress === 'number' && progress === 100,
            ),
        [courseProgressById],
    );
    const {certificateById, trackerCertificate} = useCourseCertificateUrlById(
        courseIds,
        hasCompletedCourses,
        courseById,
        courseProgressById,
        accelerator.numericId,
    );

    const {steps: curriculumSteps} = gwgAcceleratorToLearningPath(
        accelerator,
        enrolled,
        courseTakingById,
        courseProgressById,
        certificateById,
    );

    const steps = accelerator.steps;
    const {gettext} = useI18n();
    const planStats = useMemo(() => computePlanStats(allGwgAccelerators), [allGwgAccelerators]);
    const planPreference = useGwgPlanPreference();

    // Calculate progress data for hero
    const totalCourses = courseIds.length;
    const completedCourses = useMemo(
        () => getCompletedCoursesCount(courseProgressById),
        [courseProgressById],
    );

    // Find first incomplete course
    const {nextCourseUrl, nextCourseNumber, nextCourseProgress} = useMemo(
        () => findFirstIncompleteCourse(courseIds, courseProgressById, courseTakingById),
        [courseIds, courseProgressById, courseTakingById],
    );

    return (
        <GwgCheckoutProvider planPreference={planPreference}>
            <div>
                <PathsLpHero
                    title={accelerator.career.name}
                    description={accelerator.primaryDescription}
                    accelerator={accelerator}
                    instructor={instructor}
                    isEnrolled={enrolled}
                    totalCourses={totalCourses}
                    completedCourses={completedCourses}
                    certificate={trackerCertificate}
                    nextCourseUrl={nextCourseUrl}
                    nextCourseNumber={nextCourseNumber}
                    nextCourseProgress={nextCourseProgress}
                />

                {!enrolled && (
                    <div className={'ud-container'}>
                        <LearningStepsSection steps={steps} />
                    </div>
                )}

                {!enrolled && <TopicLinks topics={topics} />}

                <div className={styles.container}>
                    <CourseCurriculums
                        academyDescription={accelerator?.secondaryDescription}
                        steps={curriculumSteps}
                        isEnrolled={enrolled}
                        certificateTitle={accelerator.career.name}
                        certificateInfo={accelerator.certificateInfo}
                        trackerCertificate={trackerCertificate ?? undefined}
                    />

                    {!isGwgSubscriber && (
                        <div className="ud-container">
                            <GrowWithGoogleBannerWrapper
                                planStats={planStats}
                                learnMoreUrl={'/partner/google/learn-ai/'}
                            />
                        </div>
                    )}
                </div>

                {!enrolled && (
                    <div className={'ud-container'}>
                        <div className={styles['testimonials-container']}>
                            <GrowWithGoogleTestimonials
                                title={
                                    accelerator.testimonialHeading ??
                                    gettext('What other learners have to say')
                                }
                                testimonials={accelerator.testimonials || []}
                            />
                        </div>
                    </div>
                )}

                <GwgFooter
                    instructor={instructor}
                    frequentlyAskedQuestions={accelerator.frequentlyAskedQuestions || []}
                />
            </div>
        </GwgCheckoutProvider>
    );
}
